import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import {
  NPC_NAME, INTRO_LINES, CHAT_SCRIPT,
  DECISION_PROMPT, POST_CHOICE_MESSAGE, OUTRO_LINES,
} from "../npcScript";
import MoveIcon from "./MoveIcon";
import AvatarRing from "./AvatarRing";
import { TRUST_COLOR, BETRAY_COLOR, TRUST_BG, BETRAY_BG, MIXED_BG, MIXED_COLOR, TEXT_DARK, TEXT_MUTED, BORDER, GRADIENT_BG } from "../theme";

const OUTCOMES = {
  both_trust:        { text: "you both trusted",   color: TRUST_COLOR,   bg: TRUST_BG,   myScore: 3, npcScore: 3 },
  both_betray:       { text: "you both betrayed",  color: BETRAY_COLOR,  bg: BETRAY_BG,  myScore: 1, npcScore: 1 },
  i_trust_they_betray: { text: "you were betrayed", color: MIXED_COLOR,  bg: MIXED_BG,   myScore: 0, npcScore: 5 },
  i_betray_they_trust: { text: "you betrayed them", color: MIXED_COLOR,  bg: MIXED_BG,   myScore: 5, npcScore: 0 },
};

function NpcAvatar({ size = 40 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: GRADIENT_BG,
      display: "flex", alignItems: "center", justifyContent: "center",
      border: `2px solid ${BORDER}`, overflow: "hidden",
    }}>
      <img src="/favicon.svg" alt={NPC_NAME} style={{ width: "72%", height: "72%" }} />
    </div>
  );
}

function TypingDots() {
  return (
    <div style={{ padding: "9px 14px", background: "#f3f5ff", borderRadius: "14px 14px 14px 4px", display: "inline-block" }}>
      <div className="typing-dots">
        <span /><span /><span />
      </div>
    </div>
  );
}

export default function TutorialGame({ userProfile, onClose, onPlay }) {
  // "intro" → "chat" → "deciding" → "reveal" → "done"
  const [phase, setPhase] = useState("intro");
  const [messages, setMessages] = useState([]);
  const [scriptIndex, setScriptIndex] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [myChoice, setMyChoice] = useState(null);
  const [npcChoice, setNpcChoice] = useState(null);
  const [typed, setTyped] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showTyping, phase]);

  // Transition from intro to chat after a beat
  useEffect(() => {
    if (phase !== "intro") return;
    const t = setTimeout(() => setPhase("chat"), 2000);
    return () => clearTimeout(t);
  }, [phase]);

  // Play through the script one message at a time
  useEffect(() => {
    if (phase !== "chat") return;
    if (scriptIndex >= CHAT_SCRIPT.length) {
      const t = setTimeout(() => setPhase("deciding"), 600);
      return () => clearTimeout(t);
    }
    const entry = CHAT_SCRIPT[scriptIndex];
    setShowTyping(true);
    const total = entry.delay;

    const tShow = setTimeout(() => {
      setShowTyping(false);
      setMessages((prev) => [...prev, { from: "npc", text: entry.text }]);
      setScriptIndex((i) => i + 1);
    }, total);

    // Hide typing indicator a little before the message appears
    const tHide = setTimeout(() => setShowTyping(false), total - 80);

    return () => { clearTimeout(tShow); clearTimeout(tHide); };
  }, [phase, scriptIndex]);

  const sendMessage = () => {
    const t = typed.trim();
    if (!t) return;
    setMessages((prev) => [...prev, { from: "me", text: t }]);
    setTyped("");
  };

  const handleChoice = (choice) => {
    setMyChoice(choice);
    setMessages((prev) => [...prev, { from: "me", text: choice === "cooperate" ? "i trust you" : "i betray" }]);
    setPhase("reveal");
    const npc = Math.random() < 0.5 ? "cooperate" : "defect";
    setTimeout(() => {
      setNpcChoice(npc);
      setPhase("done");
    }, 2200);
  };

  const outcomeKey = myChoice && npcChoice
    ? myChoice === "cooperate" && npcChoice === "cooperate" ? "both_trust"
    : myChoice === "defect"    && npcChoice === "defect"    ? "both_betray"
    : myChoice === "cooperate" ? "i_trust_they_betray"
    : "i_betray_they_trust"
    : null;
  const outcome = outcomeKey ? OUTCOMES[outcomeKey] : null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#fff", display: "flex", flexDirection: "column", zIndex: 200 }}>

      {/* ── Header ── */}
      <div style={{
        padding: "12px 16px", borderBottom: `1px solid ${BORDER}`,
        display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <NpcAvatar size={34} />
          <div>
            <div style={{ fontSize: "0.8125rem", fontWeight: "700" }}>{NPC_NAME}</div>
            <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>practice round</div>
          </div>
        </div>
        <div style={{ textAlign: "center", fontSize: "0.75rem", color: TEXT_MUTED, fontWeight: "600" }}>
          how to play
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "none", border: "none", padding: "6px", display: "flex" }}>
            <X size={20} color={TEXT_MUTED} />
          </button>
        </div>
      </div>

      {/* ── Intro screen ── */}
      {phase === "intro" && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "18px", padding: "32px 24px" }}>
          <NpcAvatar size={80} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "1.0625rem", fontWeight: "700", marginBottom: "10px" }}>
              you've been matched with {NPC_NAME}
            </div>
            {INTRO_LINES.map((line, i) => (
              <div key={i} style={{ fontSize: "0.8125rem", color: TEXT_MUTED, lineHeight: 1.6 }}>{line}</div>
            ))}
          </div>
        </div>
      )}

      {/* ── Chat area ── */}
      {phase !== "intro" && (
        <div style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {messages.map((msg, i) => {
            const isMe = msg.from === "me";
            return (
              <div key={i} style={{ display: "flex", alignItems: "flex-end", gap: "8px", flexDirection: isMe ? "row-reverse" : "row" }}>
                {!isMe && <NpcAvatar size={28} />}
                <div style={{
                  maxWidth: "78%", padding: "9px 13px",
                  borderRadius: isMe ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                  background: isMe ? TRUST_COLOR : "#f3f5ff",
                  color: isMe ? "#fff" : TEXT_DARK,
                  fontSize: "0.875rem", lineHeight: 1.4,
                }}>
                  {msg.text}
                </div>
              </div>
            );
          })}

          {showTyping && (
            <div style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
              <NpcAvatar size={28} />
              <TypingDots />
            </div>
          )}

          {phase === "deciding" && (
            <div style={{ textAlign: "center", padding: "10px 0", fontSize: "0.8125rem", color: TEXT_MUTED, fontWeight: "600" }}>
              — {DECISION_PROMPT} —
            </div>
          )}

          {phase === "reveal" && (
            <div style={{ textAlign: "center", padding: "8px 0", fontSize: "0.8125rem", color: TEXT_MUTED }}>
              {POST_CHOICE_MESSAGE}
            </div>
          )}

          {phase === "done" && outcome && (
            <div style={{ textAlign: "center", marginTop: "10px", animation: "fadeIn 0.3s ease" }}>
              {/* Side-by-side reveal */}
              <div style={{ display: "flex", justifyContent: "center", gap: "32px", marginBottom: "18px" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                  <div style={{ position: "relative" }}>
                    <NpcAvatar size={60} />
                    <div style={{ position: "absolute", bottom: "-8px", right: "-8px", background: "#fff", borderRadius: "50%", padding: "6px", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}>
                      <MoveIcon move={npcChoice} size={18} flip={npcChoice === "defect"} />
                    </div>
                  </div>
                  <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>{NPC_NAME}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                  <div style={{ position: "relative" }}>
                    <AvatarRing photoURL={userProfile?.photoURL} size={60} />
                    <div style={{ position: "absolute", bottom: "-8px", right: "-8px", background: "#fff", borderRadius: "50%", padding: "6px", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}>
                      <MoveIcon move={myChoice} size={18} />
                    </div>
                  </div>
                  <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>you</div>
                </div>
              </div>

              {/* Outcome pill */}
              <span style={{ display: "inline-block", padding: "8px 22px", borderRadius: "20px", background: outcome.bg, color: outcome.color, fontWeight: "700", fontSize: "0.9375rem" }}>
                {outcome.text}
              </span>

              {/* Scores */}
              <div style={{ display: "flex", gap: "10px", marginTop: "14px" }}>
                <div style={{ flex: 1, padding: "12px", border: `1px solid ${BORDER}`, borderRadius: "10px" }}>
                  <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, marginBottom: "3px" }}>you</div>
                  <div style={{ fontSize: "1.375rem", fontWeight: "700" }}>+{outcome.myScore}</div>
                </div>
                <div style={{ flex: 1, padding: "12px", border: `1px solid ${BORDER}`, borderRadius: "10px" }}>
                  <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, marginBottom: "3px" }}>{NPC_NAME}</div>
                  <div style={{ fontSize: "1.375rem", fontWeight: "700" }}>+{outcome.npcScore}</div>
                </div>
              </div>

              {/* Outro flavour text */}
              {OUTRO_LINES[outcomeKey] && (
                <div style={{ marginTop: "14px", fontSize: "0.8125rem", color: TEXT_MUTED, lineHeight: 1.5, fontStyle: "italic" }}>
                  "{OUTRO_LINES[outcomeKey]}"
                </div>
              )}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      {/* ── Decision buttons ── */}
      {phase === "deciding" && (
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${BORDER}`, display: "flex", gap: "10px" }}>
          <button
            onClick={() => handleChoice("cooperate")}
            style={{ flex: 1, padding: "14px", background: TRUST_BG, color: TRUST_COLOR, border: `1.5px solid ${TRUST_COLOR}`, borderRadius: "10px", fontWeight: "700", fontSize: "0.9375rem" }}
          >
            trust
          </button>
          <button
            onClick={() => handleChoice("defect")}
            style={{ flex: 1, padding: "14px", background: BETRAY_BG, color: BETRAY_COLOR, border: `1.5px solid ${BETRAY_COLOR}`, borderRadius: "10px", fontWeight: "700", fontSize: "0.9375rem" }}
          >
            betray
          </button>
        </div>
      )}

      {/* ── Chat input (while script is running) ── */}
      {(phase === "chat" || phase === "reveal") && (
        <div style={{ padding: "10px 12px", borderTop: `1px solid ${BORDER}`, display: "flex", gap: "8px" }}>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="say something..."
            style={{ flex: 1, padding: "10px 14px", borderRadius: "22px", border: `1px solid ${BORDER}`, fontSize: "0.875rem", outline: "none" }}
          />
          <button
            onClick={sendMessage}
            style={{ padding: "10px 18px", background: TRUST_COLOR, color: "#fff", border: "none", borderRadius: "22px", fontWeight: "700", fontSize: "0.875rem" }}
          >
            send
          </button>
        </div>
      )}

      {/* ── End CTA ── */}
      {phase === "done" && (
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${BORDER}`, display: "flex", gap: "8px" }}>
          <button
            onClick={onPlay}
            style={{ flex: 2, padding: "14px", background: TRUST_COLOR, color: "#fff", border: "none", borderRadius: "10px", fontWeight: "700", fontSize: "0.9375rem" }}
          >
            play the real game
          </button>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: "14px", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "10px", fontWeight: "600" }}
          >
            back
          </button>
        </div>
      )}
    </div>
  );
}
